import type { LatLngTuple } from "@fortex/api";

export type WktGeometry =
  | { kind: "point"; point: LatLngTuple }
  | { kind: "line"; points: LatLngTuple[] }
  | { kind: "polygon"; rings: LatLngTuple[][] }
  | { kind: "multi"; parts: WktGeometry[] };

type Group = string | Group[];

function splitTopLevel(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;

  for (let index = 0; index < text.length; index++) {
    const character = text[index];

    if (character === "(") depth++;
    else if (character === ")") depth--;
    else if (character === "," && depth === 0) {
      parts.push(text.slice(start, index));
      start = index + 1;
    }
  }

  parts.push(text.slice(start));

  return parts.map(a => a.trim()).filter(a => a.length > 0);
}

function parseGroup(text: string): Group {
  const trimmed = text.trim();

  if (!trimmed.startsWith("(")) return trimmed;

  const inner = trimmed.slice(1, trimmed.lastIndexOf(")"));

  return splitTopLevel(inner).map(parseGroup);
}

function coordinate(text: string): LatLngTuple | null {
  const parts = text.trim().split(/\s+/);

  if (parts.length < 2) return null;

  const lon = Number(parts[0]);
  const lat = Number(parts[1]);

  return Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null;
}

function coordinates(group: Group): LatLngTuple[] {
  if (typeof group === "string") {
    const single = coordinate(group);

    return single === null ? [] : [single];
  }

  return group.flatMap(a => (typeof a === "string" ? coordinates(a) : []));
}

function rings(group: Group): LatLngTuple[][] {
  if (typeof group === "string") return [coordinates(group)];

  return group.map(a => coordinates(a)).filter(a => a.length > 0);
}

export function parseWkt(wkt: string | null | undefined): WktGeometry | null {
  if (!wkt) return null;

  const open = wkt.indexOf("(");

  if (open < 0) return null;

  const type = wkt
    .slice(0, open)
    .trim()
    .toUpperCase()
    .replace(/\s*(Z|M|ZM)$/, "");
  const body = parseGroup(wkt.slice(open));

  if (typeof body === "string") return null;

  switch (type) {
    case "POINT": {
      const points = coordinates(body);

      return points.length === 0 ? null : { kind: "point", point: points[0] };
    }
    case "LINESTRING": {
      const points = coordinates(body);

      return points.length < 2 ? null : { kind: "line", points };
    }
    case "POLYGON": {
      const shape = rings(body).filter(a => a.length >= 3);

      return shape.length === 0 ? null : { kind: "polygon", rings: shape };
    }
    case "MULTIPOINT": {
      const parts = body.map(a => coordinates(a)).filter(a => a.length > 0);

      return parts.length === 0 ? null : { kind: "multi", parts: parts.map(a => ({ kind: "point", point: a[0] }) as WktGeometry) };
    }
    case "MULTILINESTRING": {
      const parts = body.map(a => coordinates(a)).filter(a => a.length >= 2);

      return parts.length === 0 ? null : { kind: "multi", parts: parts.map(a => ({ kind: "line", points: a }) as WktGeometry) };
    }
    case "MULTIPOLYGON": {
      const parts = body.map(a => rings(a).filter(b => b.length >= 3)).filter(a => a.length > 0);

      return parts.length === 0 ? null : { kind: "multi", parts: parts.map(a => ({ kind: "polygon", rings: a }) as WktGeometry) };
    }
    default:
      return null;
  }
}

export function geometryPoints(geometry: WktGeometry | null): LatLngTuple[] {
  if (geometry === null) return [];

  switch (geometry.kind) {
    case "point":
      return [geometry.point];
    case "line":
      return geometry.points;
    case "polygon":
      return geometry.rings.flat();
    case "multi":
      return geometry.parts.flatMap(geometryPoints);
  }
}

export function geometrySegments(geometry: WktGeometry | null): [LatLngTuple, LatLngTuple][] {
  if (geometry === null) return [];

  switch (geometry.kind) {
    case "point":
      return [];
    case "line":
      return pairs(geometry.points, false);
    case "polygon":
      return geometry.rings.flatMap(a => pairs(a, true));
    case "multi":
      return geometry.parts.flatMap(geometrySegments);
  }
}

function pairs(points: LatLngTuple[], closed: boolean): [LatLngTuple, LatLngTuple][] {
  const result: [LatLngTuple, LatLngTuple][] = [];

  for (let index = 1; index < points.length; index++) result.push([points[index - 1], points[index]]);

  if (closed && points.length > 2) {
    const first = points[0];
    const last = points[points.length - 1];

    if (first[0] !== last[0] || first[1] !== last[1]) result.push([last, first]);
  }

  return result;
}

export function number(value: number): string {
  return Number(value.toFixed(7)).toString();
}

function pair(point: LatLngTuple): string {
  return `${number(point[1])} ${number(point[0])}`;
}

export function pointWkt(point: LatLngTuple): string {
  return `POINT(${pair(point)})`;
}

export function lineWkt(points: LatLngTuple[]): string {
  return `LINESTRING(${points.map(pair).join(", ")})`;
}

export function polygonWkt(ring: LatLngTuple[]): string {
  const closed = ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1]) ? [...ring, ring[0]] : ring;

  return `POLYGON((${closed.map(pair).join(", ")}))`;
}

export function ringPoints(ring: LatLngTuple[]): LatLngTuple[] {
  if (ring.length > 1) {
    const first = ring[0];
    const last = ring[ring.length - 1];

    if (first[0] === last[0] && first[1] === last[1]) return ring.slice(0, -1);
  }

  return ring;
}

export function geometryWkt(geometry: WktGeometry): string {
  switch (geometry.kind) {
    case "point":
      return pointWkt(geometry.point);
    case "line":
      return lineWkt(geometry.points);
    case "polygon":
      return `POLYGON(${geometry.rings.map(a => `(${(a.length > 0 && (a[0][0] !== a[a.length - 1][0] || a[0][1] !== a[a.length - 1][1]) ? [...a, a[0]] : a).map(pair).join(", ")})`).join(", ")})`;
    case "multi": {
      const kinds = new Set(geometry.parts.map(a => a.kind));

      if (kinds.size === 1 && kinds.has("point")) return `MULTIPOINT(${geometry.parts.map(a => (a.kind === "point" ? `(${pair(a.point)})` : "")).join(", ")})`;
      if (kinds.size === 1 && kinds.has("line")) return `MULTILINESTRING(${geometry.parts.map(a => (a.kind === "line" ? `(${a.points.map(pair).join(", ")})` : "")).join(", ")})`;
      if (kinds.size === 1 && kinds.has("polygon")) return `MULTIPOLYGON(${geometry.parts.map(a => (a.kind === "polygon" ? geometryWkt(a).slice("POLYGON".length) : "")).join(", ")})`;

      return `GEOMETRYCOLLECTION(${geometry.parts.map(geometryWkt).join(", ")})`;
    }
  }
}
